// src/components/schedule/ScheduleActivityLog.js
import { useState, useEffect, useRef } from 'react';
import { FiActivity, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getRecentActivityLogs, subscribeToActivityLogs } from '../../lib/supabase/activityLog';
import { useAuth } from '../../contexts/AuthContext';

const MAX_LOGS = 30;

const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const ROLE_COLOURS = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin: 'bg-blue-100 text-blue-700',
    designer: 'bg-green-100 text-green-700',
    estimator: 'bg-amber-100 text-amber-700',
    driver: 'bg-gray-100 text-gray-600',
};

/**
 * A flex-fill chat-style activity log panel intended to sit as the right-side
 * child inside a `flex flex-row justify-between` header div.
 * It stretches to match the height of its flex sibling (the title/back-button block)
 * and shows a scrollable list of recent schedule actions.
 *
 * @param {string} actionOrigin  e.g. "schedule-roof", "schedule-delivery", "schedule-sitemeasure"
 */
const ScheduleActivityLog = ({ actionOrigin }) => {
    const [logs, setLogs] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const scrollContainerRef = useRef(null);
    const channelRef = useRef(null);
    const didMount = useRef(false);
    const { company } = useAuth();

    // ── Initial fetch ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!actionOrigin || !company?.id) return;
        getRecentActivityLogs(actionOrigin, MAX_LOGS, company.id).then(setLogs);
    }, [actionOrigin, company?.id]);

    // ── Realtime subscription ─────────────────────────────────────────────────
    useEffect(() => {
        if (!actionOrigin || !company?.id) return;
        if (channelRef.current) channelRef.current.unsubscribe();

        channelRef.current = subscribeToActivityLogs(actionOrigin, company?.id, (newLog) => {
            setLogs(prev => {
                const next = [...prev, newLog];
                return next.length > MAX_LOGS ? next.slice(next.length - MAX_LOGS) : next;
            });
        });

        return () => {
            if (channelRef.current) {
                channelRef.current.unsubscribe();
                channelRef.current = null;
            }
        };
    }, [actionOrigin, company?.id]);

    // ── Auto-scroll to bottom inside the container only (no page scroll) ────
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const scrollToBottom = () => {
            el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth'
            });
        };

        if (!didMount.current) {
            didMount.current = true;
            scrollToBottom();
            return;
        }

        const timeout = setTimeout(scrollToBottom, 150); // match transition duration

        return () => clearTimeout(timeout);

    }, [logs, isOpen]);

    return (
        // self-start: shrinks to header only when closed; does not stretch to parent height
        <div className="schedule-activity self-start flex flex-col w-80 min-w-[200px] rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">

            {/* ── Header / toggle ──────────────────────────────────────────── */}
            <button
                aria-expanded={isOpen}
                onClick={() => setIsOpen(p => !p)}
                className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 hover:from-blue-50 hover:to-blue-100 transition-colors group"
            >
                <div className="flex items-center gap-2">
                    <FiActivity className="w-3 h-3 text-blue-500 group-hover:text-blue-600" />
                    <span className="text-xs font-semibold text-gray-700 group-hover:text-blue-700">
                        Activity
                    </span>
                    {logs.length > 0 && (
                        <span className="text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full px-1.5 leading-none py-0.5">
                            {logs.length}
                        </span>
                    )}
                </div>
                {isOpen
                    ? <FiChevronUp className="w-3 h-3 text-gray-400 group-hover:text-blue-500" />
                    : <FiChevronDown className="w-3 h-3 text-gray-400 group-hover:text-blue-500" />
                }
            </button>

            {/* ── Log list — fixed height, always scrollable, never grows ─── */}
            <div ref={scrollContainerRef} className={`${isOpen ? "h-64" : "h-32"} transition-all ease-in-out overflow-y-auto flex flex-col gap-0.5 p-1.5 bg-white`}>
                {logs.length === 0 ? (
                    <p className="text-[10px] text-gray-400 text-center py-4 select-none">
                        No activity yet
                    </p>
                ) : (
                    logs.map((log) => (
                        <div
                            key={log.id}
                            className="text-[10px] text-gray-700 px-2 py-1 rounded-md bg-gray-50 border border-gray-100 flex-shrink-0"
                        >
                            <div className="flex items-center gap-1 flex-wrap">
                                <span className="font-semibold text-gray-900 leading-tight">
                                    {log.user_name}
                                </span>
                                {log.user_role && (
                                    <span className={`text-[9px] font-bold px-1 rounded leading-none py-px ${ROLE_COLOURS[log.user_role] || 'bg-gray-100 text-gray-600'}`}>
                                        {log.user_role.replace('_', ' ')}
                                    </span>
                                )}
                                <span className="text-[9px] text-gray-400 ml-auto">{formatTime(log.timestamp)}</span>
                            </div>
                            <p className="text-gray-600 mt-0.5 leading-tight">{log.details}</p>
                        </div>
                    ))
                )}
            </div>

        </div>
    );
};

export default ScheduleActivityLog;
